import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAgentRefresh, QUERY_KEYS } from '../ai/hooks';
import { AGENT_FOCUS_EVENT } from '../ai/context/AIAgentContext';
import type { UIFocus } from '../ai/types/agent';
import type {
  CalendarItem,
  CalendarView,
  CalendarEvent,
  CreateCalendarEventInput,
} from '../types/calendar';
import {
  CALENDAR_VIEW_LABELS,
  MONTH_LABELS,
  getStartOfMonth,
  getEndOfMonth,
  getStartOfWeek,
  getEndOfWeek,
  addMonths,
  addWeeks,
} from '../types/calendar';
import {
  getCalendarItems,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getCalendarEventById,
} from '../services/calendarService';
import { CalendarMonthView } from '../components/calendar/CalendarMonthView';
import { CalendarWeekView } from '../components/calendar/CalendarWeekView';
import { CalendarEventModal } from '../components/calendar/CalendarEventModal';
import { logger } from '../lib/logger';

export function Calendar() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('week');
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>();

  // Focus state for AI Agent highlighting
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);

  // Calculate date range based on view
  const getDateRange = useCallback(() => {
    if (view === 'month') {
      const monthStart = getStartOfMonth(currentDate);
      const monthEnd = getEndOfMonth(currentDate);
      // Include days from prev/next month that appear in the view
      return {
        start: getStartOfWeek(monthStart, true),
        end: getEndOfWeek(monthEnd, true),
      };
    } else {
      return {
        start: getStartOfWeek(currentDate, true),
        end: getEndOfWeek(currentDate, true),
      };
    }
  }, [currentDate, view]);

  // Load calendar items
  const loadItems = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { start, end } = getDateRange();
      const data = await getCalendarItems(user.id, start, end);
      setItems(data);
    } catch (error) {
      logger.error('Failed to load calendar items:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, getDateRange]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Listen for AI Agent calendar updates
  useAgentRefresh([QUERY_KEYS.CALENDAR], loadItems);

  // Listen for AI Agent focus events
  useEffect(() => {
    const handleFocus = (event: Event) => {
      const customEvent = event as CustomEvent<UIFocus>;
      const focus = customEvent.detail;

      if (focus.type === 'event') {
        setFocusedEventId(focus.id);

        // Clear focus highlight after 3 seconds
        setTimeout(() => {
          setFocusedEventId(null);
        }, 3000);
      }
    };

    window.addEventListener(AGENT_FOCUS_EVENT, handleFocus);
    return () => window.removeEventListener(AGENT_FOCUS_EVENT, handleFocus);
  }, []);

  // Navigation
  const goToPrevious = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  const goToNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format header title
  const getHeaderTitle = () => {
    if (view === 'month') {
      return `${MONTH_LABELS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else {
      const weekStart = getStartOfWeek(currentDate, true);
      const weekEnd = getEndOfWeek(currentDate, true);
      const startMonth = MONTH_LABELS[weekStart.getMonth()];
      const endMonth = MONTH_LABELS[weekEnd.getMonth()];

      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${weekStart.getDate()}. - ${weekEnd.getDate()}. ${startMonth} ${weekStart.getFullYear()}`;
      } else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
        return `${weekStart.getDate()}. ${startMonth} - ${weekEnd.getDate()}. ${endMonth} ${weekStart.getFullYear()}`;
      } else {
        return `${weekStart.getDate()}. ${startMonth} ${weekStart.getFullYear()} - ${weekEnd.getDate()}. ${endMonth} ${weekEnd.getFullYear()}`;
      }
    }
  };

  // Handle day click - open modal to create new event
  const handleDayClick = (date: Date) => {
    setSelectedEvent(null);
    setDefaultDate(date);
    setIsModalOpen(true);
  };

  // Handle item click
  const handleItemClick = async (item: CalendarItem) => {
    if (item.source === 'todo') {
      // Navigate to todos page (could also open a modal)
      navigate('/todos');
    } else {
      // Open event modal for editing
      if (!user) return;

      try {
        // Get the original event ID (for recurring instances)
        const eventId = item.originalEventId || item.id;
        const event = await getCalendarEventById(eventId, user.id);
        if (event) {
          setSelectedEvent(event);
          setDefaultDate(undefined);
          setIsModalOpen(true);
        }
      } catch (error) {
        logger.error('Failed to load event:', error);
      }
    }
  };

  // Handle save event
  const handleSaveEvent = async (input: CreateCalendarEventInput) => {
    if (!user) return;

    try {
      if (selectedEvent) {
        await updateCalendarEvent(selectedEvent.id, user.id, input);
      } else {
        await createCalendarEvent(user.id, input);
      }
      await loadItems();
    } catch (error) {
      logger.error('Failed to save event:', error);
      throw error;
    }
  };

  // Handle delete event
  const handleDeleteEvent = async (eventId: string) => {
    if (!user) return;

    try {
      await deleteCalendarEvent(eventId, user.id);
      await loadItems();
    } catch (error) {
      logger.error('Failed to delete event:', error);
      throw error;
    }
  };

  // Handle new event button
  const handleNewEvent = () => {
    setSelectedEvent(null);
    setDefaultDate(new Date());
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Kalender</h1>
        <button
          data-tour="create-event"
          onClick={handleNewEvent}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          Neuer Termin
        </button>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevious}
              className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToNext}
              className="p-2 text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              Heute
            </button>
            <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white ml-2">
              {getHeaderTitle()}
            </h2>
          </div>

          {/* View Switcher */}
          <div data-tour="calendar-view" className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
            {(['month', 'week'] as CalendarView[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === v
                    ? 'bg-white dark:bg-slate-600 text-[#1A1A1A] dark:text-white shadow-sm'
                    : 'text-[#6B6B6B] dark:text-slate-400 hover:text-[#1A1A1A] dark:hover:text-white'
                }`}
              >
                {CALENDAR_VIEW_LABELS[v]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {isLoading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : view === 'month' ? (
        <CalendarMonthView
          currentDate={currentDate}
          items={items}
          onDayClick={handleDayClick}
          onItemClick={handleItemClick}
          focusedEventId={focusedEventId}
        />
      ) : (
        <CalendarWeekView
          currentDate={currentDate}
          items={items}
          onDayClick={handleDayClick}
          onItemClick={handleItemClick}
          focusedEventId={focusedEventId}
        />
      )}

      {/* Legend */}
      <div data-tour="calendar-types" className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#6B6B6B]">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-violet-100 border border-violet-300"></div>
          <span>To-Do</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></div>
          <span>Termin</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
          <span>Anruf</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></div>
          <span>Meeting</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></div>
          <span>Erinnerung</span>
        </div>
      </div>

      {/* Event Modal */}
      <CalendarEventModal
        isOpen={isModalOpen}
        event={selectedEvent}
        defaultDate={defaultDate}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEvent(null);
          setDefaultDate(undefined);
        }}
        onSave={handleSaveEvent}
        onDelete={selectedEvent ? handleDeleteEvent : undefined}
      />
    </div>
  );
}
